// The table as an installed web app (issue 1182): the service worker that installing and push need, and the opt-in
// that sends this device's push subscription to the server. No store app, no fee: a browser's own "Install" / "Add to
// Home Screen". The worker (`public/sw.js`) caches nothing — no API answer, no picture, not even the app shell — so a
// deploy is never hidden behind a stale copy and no bearer-bound answer is ever kept on the device.
import { useEffect, useState } from 'react';
import { requestJson } from './api';

/** Register the worker, once, where the browser has them. Its scope is the whole origin, so a tap on a notice opens any table. */
export function registerServiceWorker(nav: Navigator = navigator): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in nav)) return Promise.resolve(null);
  return nav.serviceWorker.register('/sw.js', { scope: '/' }).catch(() => null);
}

/** An iPhone or iPad's browser (any of them is Safari underneath): push reaches it only once the table is on the Home Screen. */
export function isIos(nav: Pick<Navigator, 'userAgent' | 'maxTouchPoints'> & { platform?: string } = navigator): boolean {
  // An iPad says it is a Mac; a Mac has no touch screen.
  return /iPhone|iPad|iPod/.test(nav.userAgent) || (/Macintosh/.test(nav.userAgent) && (nav.maxTouchPoints ?? 0) > 1);
}

/** Opened from the Home Screen (or installed on a computer): the page runs on its own, without the browser's bars. */
export function isStandalone(win: Window = window): boolean {
  const iosStandalone = (win.navigator as Navigator & { standalone?: boolean }).standalone === true;
  return iosStandalone || (typeof win.matchMedia === 'function' && win.matchMedia('(display-mode: standalone)').matches);
}

/** The words where notifications are turned on, for an iPhone or iPad not yet on the Home Screen. */
export const IOS_HINT = 'On an iPhone or iPad, notifications work only from the Home Screen: tap Share, then "Add to Home Screen", open the table from there, and turn this on again.';

export type PushState =
  | { kind: 'unsupported' } // no service worker or Push API in this browser
  | { kind: 'ios-browser' } // an iPhone or iPad in Safari's tab: Add to Home Screen first
  | { kind: 'off-server' } // the server has no keys: hidden
  | { kind: 'blocked' } // the person said no to this browser's question
  | { kind: 'ready'; on: boolean; publicKey: string };

const urlBase64ToBytes = (b64: string): Uint8Array<ArrayBuffer> => {
  const pad = '='.repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
};

interface Env { nav: Navigator; win: Window; fetchImpl: typeof fetch }
const defaultEnv = (): Env => ({ nav: navigator, win: window, fetchImpl: fetch });

/** Where this device stands: whether push can be offered here, and whether it is on. */
export async function pushState(env: Env = defaultEnv()): Promise<PushState> {
  const { nav, win, fetchImpl } = env;
  const hasPush = 'serviceWorker' in nav && 'PushManager' in win && typeof (win as Window & { Notification?: unknown }).Notification !== 'undefined';
  if (!hasPush) return isIos(nav) && !isStandalone(win) ? { kind: 'ios-browser' } : { kind: 'unsupported' };
  const { status, body } = await requestJson('/api/push', { method: 'GET' }, fetchImpl);
  const b = body as { enabled?: unknown; publicKey?: unknown } | null;
  if (status !== 200 || b?.enabled !== true || typeof b.publicKey !== 'string') return { kind: 'off-server' };
  if (Notification.permission === 'denied') return { kind: 'blocked' };
  const reg = await nav.serviceWorker.getRegistration('/');
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  // On only when the server holds this browser's endpoint for THIS account: another account may have subscribed in
  // this browser, and its subscription is not this person's.
  let mine = false;
  if (sub !== null && Notification.permission === 'granted') {
    const list = await requestJson('/api/push/subscriptions', { method: 'GET' }, fetchImpl);
    const devices = (list.body as { devices?: Array<{ endpoint?: unknown }> } | null)?.devices;
    mine = list.status === 200 && Array.isArray(devices) && devices.some((d) => d.endpoint === sub.endpoint);
  }
  return { kind: 'ready', on: mine, publicKey: b.publicKey };
}

/** A short name for this device in the person's list, from what the browser says it is; never sent anywhere else. */
export function deviceLabel(nav: Pick<Navigator, 'userAgent' | 'maxTouchPoints'> = navigator): string {
  const ua = nav.userAgent;
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua) || (/Macintosh/.test(ua) && (nav.maxTouchPoints ?? 0) > 1)) return 'iPad';
  if (/Android/.test(ua)) return /Mobile/.test(ua) ? 'Android phone' : 'Android tablet';
  if (/Windows/.test(ua)) return 'Windows computer';
  if (/Macintosh/.test(ua)) return 'Mac';
  if (/Linux/.test(ua)) return 'Linux computer';
  return 'Browser';
}

/** Turn push on for this device: asked only on a click; the subscription goes to this origin's server, nowhere else. */
export async function enablePush(publicKey: string, env: Env = defaultEnv()): Promise<{ ok: true } | { ok: false; reason: string }> {
  const answer = Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission();
  if (answer !== 'granted') return { ok: false, reason: answer === 'denied' ? 'Notifications are blocked for this site in your browser settings.' : 'Notifications were not allowed.' };
  const reg = (await env.nav.serviceWorker.getRegistration('/')) ?? (await registerServiceWorker(env.nav));
  if (!reg) return { ok: false, reason: 'This browser could not set up notifications.' };
  const ready = await env.nav.serviceWorker.ready;
  let sub = await ready.pushManager.getSubscription();
  if (!sub) {
    try { sub = await ready.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToBytes(publicKey) }); } catch { return { ok: false, reason: 'This browser could not set up notifications.' }; }
  }
  const json = sub.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
  const { status, body } = await requestJson('/api/push/subscriptions', { method: 'POST', body: JSON.stringify({ endpoint: json.endpoint, keys: json.keys, label: deviceLabel(env.nav) }) }, env.fetchImpl);
  if (status === 201) return { ok: true };
  const reason = (body as { reason?: unknown } | null)?.reason;
  return { ok: false, reason: typeof reason === 'string' ? reason : 'Notifications could not be turned on just now.' };
}

/** Turn push off for this device: the server forgets it, and the browser drops the subscription. */
export async function disablePush(env: Env = defaultEnv()): Promise<void> {
  const reg = await env.nav.serviceWorker.getRegistration('/');
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  if (!sub) return;
  await requestJson('/api/push/subscriptions', { method: 'DELETE', body: JSON.stringify({ endpoint: sub.endpoint }) }, env.fetchImpl);
  await sub.unsubscribe().catch(() => false);
}

/**
 * "Notify me on this device": the per-device opt-in, beside the in-page "Notify me when it's my turn". Hidden when the
 * server has no keys or the browser has no push; on an iPhone or iPad in a browser tab it says, in plain words, to add
 * the table to the Home Screen first.
 */
export function PushToggle({ env }: { env?: Env }) {
  const [state, setState] = useState<PushState | null>(null);
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    // Nothing is sent on a visit: only a click subscribes. The server keeps one row per endpoint, so a click here may
    // take this browser's endpoint over from another account that used it — the person's own choice on their device.
    void pushState(env).then((s) => { if (live) setState(s); }).catch(() => { if (live) setState({ kind: 'unsupported' }); });
    return () => { live = false; };
  }, [env]);
  if (!state || state.kind === 'unsupported' || state.kind === 'off-server') return null;
  if (state.kind === 'ios-browser') return <p className="text-xs text-stone-400" role="note">{IOS_HINT}</p>;
  if (state.kind === 'blocked') return <p className="text-xs text-stone-500">Notifications on this device: blocked in this browser&apos;s settings.</p>;
  const toggle = async () => {
    setBusy(true); setSaid(null);
    try {
      if (state.on) { await disablePush(env); setState({ ...state, on: false }); return; }
      const r = await enablePush(state.publicKey, env);
      if (r.ok) setState({ ...state, on: true }); else setSaid(r.reason);
    } finally { setBusy(false); }
  };
  return (
    <div className="text-xs text-stone-400">
      <label className="flex items-center gap-1" title="Your turn and invitations reach this device even when the table is closed. Nothing is sent while the table is open in front of you.">
        <input type="checkbox" aria-label="Notify me on this device" checked={state.on} disabled={busy} onChange={() => { void toggle(); }} /> Notify me on this device
      </label>
      {said && <p role="status" className="text-amber-400">{said}</p>}
    </div>
  );
}
